import React, { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { RotatingLines } from 'react-loader-spinner'
import ReactPlayer from 'react-player'
import { Swiper, SwiperSlide } from 'swiper/react'

import icons from 'assets/icons'
import useLanguage from 'hooks/useLanguage'
import useShips from 'hooks/useShips'
import Cruise from 'types/Cruise'
import { unCapitalize } from 'utils/string'
import { isCruise } from 'utils/types'

import Ship from '../types/Ship'
import Close from './Close'
import ContentText from './ContentText'
import CruiseItem from './CruiseItem'
import Image from './Image'
import NextCruiseItem from './NextCruiseItem'
import SwiperButtons from './SwiperButtons'

import 'swiper/css'

type Props = {
  item: Ship

  closeModal: () => void

  setCruise: (cruise: Cruise) => void

  cleanItem: () => void
}

const queryParams = new URLSearchParams(window.location.search)
const isRoadShow = queryParams.get('roadShow') === 'true'

const ShipInfo: React.FC<Props> = ({ closeModal, item, setCruise, cleanItem }: Props) => {
  const { t } = useTranslation()
  const language = useLanguage()
  const [showPlayer, setShowPlayer] = useState(false)
  const [show360, setShow360] = useState(false)
  const [loadingPlayer, setLoadingPlayer] = useState(false)
  const [showVideo, setShowVideo] = useState(false)

  const { ships } = useShips()

  useEffect(() => {
    setShowPlayer(false)
    setShow360(false)
  }, [item])

  const onClickPlayer = useCallback(() => {
    setLoadingPlayer(true)
    setShowPlayer(true)
  }, [])

  const onClick360 = useCallback(() => {
    setLoadingPlayer(true)
    setShow360(true)
  }, [])

  const close360 = useCallback(() => {
    setShowPlayer(false)
    setShow360(false)
    setShowVideo(false)
  }, [])

  const onClick360Video = useCallback(() => {
    setShowVideo(true)
  }, [])

  const getShipName = useCallback(() => {
    const index = ships.findIndex((value: Ship) => value.code === item.code)
    if (index !== -1) {
      return unCapitalize(ships[index].name)
    }
  }, [item, ships])

  const onReadyPlayer = useCallback(() => {
    setLoadingPlayer(false)
  }, [])

  return (
    <div
      className={
        item === null ? 'pano--unshown' : show360 || showPlayer || showVideo ? 'pano--full' : 'pano'
      }
    >
      {!show360 && !showPlayer && !showVideo && (
        <div className='modal--closeDesktop' onClick={closeModal}>
          <Close />
        </div>
      )}

      {(show360 || showPlayer || showVideo) && (
        <div className='modal--wrapper'>
          <div className='modal--frame--return' onClick={close360}>
            <img src={icons.back} alt='back' className='modal--frame--return--icon' />
            <p className='text text--nomargin'>{t('ship.return')}</p>
          </div>
          {show360 && (
            <div className='modal--frame'>
              {loadingPlayer && (
                <div
                  className='modal--frame--loading'
                  style={{ visibility: loadingPlayer ? 'visible' : 'hidden' }}
                >
                  <RotatingLines strokeColor='#22A6BA' />
                </div>
              )}
              <iframe
                title={`${item.code}-360`}
                src={item.url360[language]}
                width='100%'
                height='100%'
                onLoad={onReadyPlayer}
              />
            </div>
          )}

          {showPlayer && (
            <div className='modal--video'>
              <p className='text text--size20 text--center'>
                {t('modal.realTimeCheck')}
              </p>
              <div style={{ height: 'inherit' }}>
                {loadingPlayer && (
                  <div
                    className='modal--frame--loading'
                    style={{ visibility: loadingPlayer ? 'visible' : 'hidden' }}
                  >
                    <RotatingLines strokeColor='#22A6BA' />
                  </div>
                )}
                <ReactPlayer
                  playing={showPlayer}
                  url={item.feedUrl}
                  width='100%'
                  height='90%'
                  onReady={onReadyPlayer}
                />
              </div>
            </div>
          )}

          {showVideo && (
            <div className='modal--video'>
              <p className='text text--size20 text--center'>
                {t('modal.video')}
              </p>
              <div style={{ height: 'inherit' }}>
                {loadingPlayer && (
                  <div
                    className='modal--frame--loading'
                    style={{ visibility: loadingPlayer ? 'visible' : 'hidden' }}
                  >
                    <RotatingLines strokeColor='#22A6BA' />
                  </div>
                )}
                <ReactPlayer
                  playing={showVideo}
                  url={item.url360video[language]}
                  width='100%'
                  height='90%'
                  onReady={onReadyPlayer}
                  controls
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`modal--scroll ${show360 || showPlayer || showVideo ? 'hide' : ''}`}>
        <div style={{ padding: '24px' }}>
          <div className='modal--close' onClick={closeModal}>
            <img src={icons.cross} alt='close' width={15} height={15} />
          </div>
          <div className='modal--container'>
            <div className='modal--header modal--header--shipHeight'>
              <Image className='modal--img' src={item.ship_images[language]} alt='img' />
              <div className='modal--header--layer'>
                <div className='modal--actions'>
                  {item.feedUrl && (
                    <div className='modal--actions--item' onClick={onClickPlayer}>
                      <img className='modal--actions--item--image' src={icons.play} alt='play' />
                      <p className='text text--size14 text--white text--center'>
                        {t('modal.webcam')}
                      </p>
                    </div>
                  )}
                  {item.url360 && item.url360[language] !== '' && !isRoadShow && (
                    <div className='modal--actions--item' onClick={onClick360}>
                      <img className='modal--actions--item--image' src={icons.round} alt='360' />
                      <p className='text text--size14 text--white text--center'>
                        {t('modal.visit')}
                      </p>
                    </div>
                  )}
                  {item.url360video && item.url360video[language] !== '' && (
                    <div className='modal--actions--item' onClick={onClick360Video}>
                      <img
                        className='modal--actions--item--image'
                        src={icons.play}
                        alt='360video'
                      />
                      <p className='text text--weight600 text--size12 text--white text--center'>
                        {t('modal.video')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={'modal--title'}>
              <div className='text text--size48 modal--title--name text--center'>
                {unCapitalize(item.name)}
              </div>
              <div className='text text--size14 text--center'>{item.infos[language]}</div>
            </div>

            <div className='modal--content--container'>
              <ContentText
                content={item.descriptions[language]}
                fullScreen={showPlayer || show360}
              />

              {!isRoadShow ? (
                <div className='modal--ship--button'>
                  <a
                    className='text text--size14 text--white button--dark button--linkoutside text--center button'
                    href={item.ship_url}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {t('ship.more', { name: unCapitalize(item.name) })}
                  </a>
                </div>
              ) : (
                <div className='modal--ship--separator' />
              )}

              {isCruise(item.cruise_current) && (
                <CruiseItem
                  pending
                  cruise={item.cruise_current}
                  setCruise={setCruise}
                  cleanItem={cleanItem}
                />
              )}
            </div>
          </div>
          {item.cruise_next.length !== 0 && (
            <>
              {item.cruise_next.length > 1 ? (
                <Swiper
                  slidesPerView={'auto'}
                  spaceBetween={20}
                  className='modal--nextCruise'
                  onSwiper={(swiper) => {
                    // Type assertion to 'any' to bypass the type checking error
                    (swiper as any).snapGrid = [...(swiper as any).slidesGrid];
                  }}
                  onReachEnd={(swiper) => {
                    (swiper as any).snapGrid = [...(swiper as any).slidesGrid];
                  }}
                >
                  <div className='modal--nextCruise--header'>
                    <p className='text text--size24 text--nomargin'>{t('ship.nextCruiseMulti')}</p>
                    {item.cruise_next.length > 1 && <SwiperButtons />}
                  </div>
                  {item.cruise_next.map((cruise: Cruise) => (
                    <SwiperSlide key={cruise.cruise_id}>
                      <NextCruiseItem
                        cruise={cruise}
                        shipName={unCapitalize(item.name)}
                        isRoadShow={isRoadShow}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <div className='modal--cruise--div modal--cruise--single'>
                  <div style={{ width: '100%' }}>
                    <p className='text text--size24 text--nomargin'>{t('ship.nextCruise')}</p>
                    <NextCruiseItem
                      cruise={item.cruise_next[0]}
                      shipName={getShipName()}
                      isRoadShow={isRoadShow}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {item.cruise_next.length !== 0 && !isRoadShow && (
            <div className='modal--cruise--all'>
              <a
                className='text text--size14 text--white button--dark button--linkoutside text--center button'
                href={item.ship_cruises_url}
                target='_blank'
                rel='noreferrer'
              >
                {t('ship.allCruises')}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ShipInfo
